import ellipse_2_green from 'assets/icons/ellipse-2-green.svg'
import ellipse_2_red from 'assets/icons/ellipse-2-red.svg'
import re_sync_connection_icon from 'assets/icons/re-sync-connection-icon.svg'
import { ReactComponent as InfoIcon } from 'assets/icons/info-icon.svg'
import { TableColumn } from 'components/Table/Table'
import { formatDate } from 'utils'
import { ConnectionStatus } from 'api/partners/connectionStatus'
import { capitalize } from 'lodash'
import { Tooltip } from '@mui/material'

import * as Styled from '../styles'

export const getPosConnectionColumns = (
  onRefresh: (connectionStatus: ConnectionStatus) => Promise<void>,
  isRefreshing?: string | null,
): Array<TableColumn<ConnectionStatus>> => [
  {
    dataIndex: 'integration',
    key: 'integration',
    title: 'Integration',
    render: (row) => {
      const posName = row.included?.find((inc: any) => inc.type === 'point_of_sale')?.attributes?.name || 'POS'
      return (
        <Styled.MenuContainer>
          {posName}
        </Styled.MenuContainer>
      )
    },
  },
  {
    dataIndex: 'status',
    key: 'status',
    title: (
      <Styled.ColumnHeaderWithInfo>
        Status
        <Tooltip 
          title="Tells you if the connection is turned on from the Chowly side. If you believe this is incorrect, please reach out to Chowly support."
          placement="top"
          slotProps={{
            tooltip: {
              sx: {
                backgroundColor: '#FAFAFA',
                color: '#292929',
                fontSize: '12px',
                fontFamily: 'Inter',
                fontWeight: 700,
                borderRadius: '12px',
                boxShadow: '0px 4px 60px 0px rgba(0, 0, 0, 0.10)',
                maxWidth: '250px',
                padding: '10px',
              }
            }
          }}
        >
          <Styled.InfoIconStyled as={InfoIcon} />
        </Tooltip>
      </Styled.ColumnHeaderWithInfo>
    ),
    render: (row) => (
      <Styled.StatusContainer>
        <Styled.StatusIcon 
          src={row.attributes.connection_status === 'CONNECTED' ? ellipse_2_green : ellipse_2_red} 
        />
        <Styled.ConnectionStatusText isSuccess={row.attributes.connection_status === 'CONNECTED'}>
          {capitalize(row.attributes.connection_status?.toLowerCase())}
        </Styled.ConnectionStatusText>
      </Styled.StatusContainer>
    ),
  },
  {
    dataIndex: 'operational',
    key: 'operational',
    title: (
      <Styled.ColumnHeaderWithInfo>
        Operational
        <Tooltip 
          title="Tells you if the connection is working right now from the connected party."
          placement="top"
          slotProps={{
            tooltip: {
              sx: {
                backgroundColor: '#FAFAFA',
                color: '#292929',
                fontSize: '12px',
                fontFamily: 'Inter',
                fontWeight: 700,
                borderRadius: '12px',
                boxShadow: '0px 4px 60px 0px rgba(0, 0, 0, 0.10)',
                maxWidth: '250px',
                padding: '10px',
              }
            }
          }}
        >
          <Styled.InfoIconStyled as={InfoIcon} />
        </Tooltip>
      </Styled.ColumnHeaderWithInfo>
    ),
    render: (row) => (
      <Styled.StatusContainer>
        <Styled.StatusIcon 
          src={row.attributes.operational_state === 'OPERATIONAL' ? ellipse_2_green : ellipse_2_red} 
        />
        <Styled.ConnectionStatusText isSuccess={row.attributes.operational_state === 'OPERATIONAL'}>
          {capitalize(row.attributes.operational_state?.toLowerCase().replace('_', ' '))}
        </Styled.ConnectionStatusText>
      </Styled.StatusContainer>
    ),
  },
  {
    dataIndex: 'last_checked',
    key: 'last_checked',
    title: 'Last Checked',
    render: (row) => (
      <Styled.MenuContainer>
        <Styled.TimestampContainer>
          <Styled.TimestampTime>
            {formatDate(row.attributes.operational_state_last_checked, 'h:mm A')}
          </Styled.TimestampTime>
          <Styled.TimestampDate>
            {formatDate(row.attributes.operational_state_last_checked, 'MM/DD/YYYY')}
          </Styled.TimestampDate>
        </Styled.TimestampContainer>
      </Styled.MenuContainer>
    ),
  },
  {
    dataIndex: 'refresh',
    key: 'refresh',
    title: 'Actions',
    headerAlign: 'center',
    render: (row) => (
      <Styled.Icon
        hoverEffect={true}
        src={re_sync_connection_icon}
        alt='refresh status icon'
        iconSize={'24'}
        disabled={isRefreshing === row.id}
        onClick={() => onRefresh(row)}
      />
    ),
  },
]

export const getMobilePosConnectionColumns = (
  onRefresh: (connectionStatus: ConnectionStatus) => Promise<void>,
  isRefreshing?: string | null,
): Array<TableColumn<ConnectionStatus>> => [
  {
    dataIndex: 'name',
    key: 'name',
    title: 'Integration',
    render: (row) => {
      const posName = row.included?.find((inc: any) => inc.type === 'point_of_sale')?.attributes?.name || 'POS'
      return (
        <Styled.MobilePlatformContainer>
          {posName}
          <Styled.Icon
            hoverEffect={true}
            src={re_sync_connection_icon}
            alt='refresh status icon'
            iconSize={'24'}
            disabled={isRefreshing === row.id}
            onClick={() => onRefresh(row)}
          />
        </Styled.MobilePlatformContainer>
      )
    },
  },
  {
    dataIndex: 'status_info',
    key: 'status_info',
    title: 'Status Information',
    render: (row) => (
      <Styled.MobileRowContainer>
        <Styled.MobileRow>
          <Styled.HeaderCell>Status</Styled.HeaderCell>
          <Styled.StatusIcon 
            src={row.attributes.connection_status === 'CONNECTED' ? ellipse_2_green : ellipse_2_red} 
          />
          <Styled.ConnectionStatusText isSuccess={row.attributes.connection_status === 'CONNECTED'}>
            {capitalize(row.attributes.connection_status?.toLowerCase())}
          </Styled.ConnectionStatusText>
        </Styled.MobileRow>
        <Styled.MobileRow>
          <Styled.HeaderCell>Operational</Styled.HeaderCell>
          <Styled.StatusIcon 
            src={row.attributes.operational_state === 'OPERATIONAL' ? ellipse_2_green : ellipse_2_red} 
          />
          <Styled.ConnectionStatusText isSuccess={row.attributes.operational_state === 'OPERATIONAL'}>
            {capitalize(row.attributes.operational_state?.toLowerCase().replace('_', ' '))}
          </Styled.ConnectionStatusText>
        </Styled.MobileRow>
        <Styled.MobileRow>
          <Styled.HeaderCell>Last Checked</Styled.HeaderCell>
          <Styled.MenuContainer>
            <Styled.MobileTimestampContainer>
              <Styled.TimestampTime>
                {formatDate(row.attributes.operational_state_last_checked, 'h:mm A')}
              </Styled.TimestampTime>
              <Styled.TimestampDate>
                {formatDate(row.attributes.operational_state_last_checked, 'MM/DD/YYYY')}
              </Styled.TimestampDate>
            </Styled.MobileTimestampContainer>
          </Styled.MenuContainer>
        </Styled.MobileRow>
      </Styled.MobileRowContainer>
    ),
  },
] 